import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useAuth } from '../../context/AuthContext'
import { useTenant } from '../../context/TenantContext'
import { Logo, PoweredByLogo } from '../common/Logo'
import api from '../../lib/api'
import { roleHomePath } from '../../lib/roleHomePath'
import { completeOnboardingSchema } from '../../lib/schemas'
import { STANDARD_FORM_OPTIONS, isSubmitDisabled } from '../../lib/formDefaults'

/**
 * First-login onboarding: set name and required password, then mark complete and go to dashboard.
 * Shown when user has onboarding_complete === false.
 */
export function CompleteOnboardingPage() {
  const navigate = useNavigate()
  const { user, userProfile, loading: authLoading, fetchUserProfile } = useAuth()
  const { tenant } = useTenant()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isValid },
    setError,
  } = useForm({
    ...STANDARD_FORM_OPTIONS,
    resolver: zodResolver(completeOnboardingSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
    },
  })

  const prefillAppliedRef = useRef(false)
  useEffect(() => {
    if (prefillAppliedRef.current || !userProfile) return
    const existingFirst = String(userProfile.first_name || '').trim()
    const existingLast = String(userProfile.last_name || '').trim()
    const full = String(userProfile.full_name || '').trim()
    const [fullFirst = '', ...rest] = full.split(/\s+/).filter(Boolean)
    const fullLast = rest.join(' ')
    const emailLocal = String(userProfile.email || user?.email || '').split('@')[0] || ''
    const normalizedLocal = emailLocal.replace(/[._-]+/g, ' ').trim()
    const fallbackFirst = normalizedLocal ? normalizedLocal.split(/\s+/)[0] : ''

    reset({
      firstName: existingFirst || fullFirst || fallbackFirst,
      lastName: existingLast || fullLast,
      password: '',
      confirmPassword: '',
    })
    prefillAppliedRef.current = true
  }, [userProfile, user?.email, reset])

  useEffect(() => {
    if (authLoading) return
    if (!user) {
      try {
        if (!localStorage.getItem('auth_token')) {
          navigate('/login', { replace: true })
        }
      } catch {
        navigate('/login', { replace: true })
      }
      return
    }
    if (userProfile?.role === 'influencer') {
      navigate('/influencer/onboarding', { replace: true })
      return
    }
    if (userProfile?.onboarding_complete) {
      navigate(roleHomePath(userProfile), { replace: true })
    }
  }, [user, userProfile, authLoading, navigate])

  const onSubmit = async (data) => {
    const body = {
      firstName: data.firstName?.trim() || undefined,
      lastName: data.lastName?.trim() || undefined,
      password: String(data.password || '').trim(),
    }
    try {
      const updated = await api.auth.completeOnboarding(body)
      await fetchUserProfile()
      navigate(roleHomePath(updated) || roleHomePath(userProfile), { replace: true })
    } catch (e) {
      setError('root', { message: e.message || 'Something went wrong. Please try again.' })
    }
  }

  if (authLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!user) {
    return null
  }

  if (userProfile?.onboarding_complete) {
    return null
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: (t) => `1px solid ${t.palette.divider}` }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            {tenant?.logoUrl ? (
              <Box component="img" src={tenant.logoUrl} alt="" sx={{ height: 52, mb: 1, objectFit: 'contain' }} />
            ) : (
              <Logo variant="lavender" height={52} sx={{ mb: 1 }} />
            )}
            {tenant?.displayName && (
              <Typography variant="subtitle1" color="text.secondary">{tenant.displayName}</Typography>
            )}
          </Box>
          <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
            Complete your profile
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Add your name and create a password to finish setup.
          </Typography>
          {errors.root && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.root.message}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="First name"
              required
              margin="normal"
              autoComplete="given-name"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              {...register('firstName')}
            />
            <TextField
              fullWidth
              label="Last name"
              margin="normal"
              autoComplete="family-name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              {...register('lastName')}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              required
              margin="normal"
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password?.message || 'At least 8 characters.'}
              {...register('password')}
            />
            <TextField
              fullWidth
              label="Confirm password"
              type="password"
              required
              margin="normal"
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3 }}
              disabled={isSubmitDisabled({ isValid, isSubmitting })}
            >
              {isSubmitting ? 'Saving...' : 'Continue'}
            </Button>
            {tenant && <PoweredByLogo />}
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}
